import { useState, useEffect, useRef } from "react";
import "./DocumentScanner.css";
import { useAuthContext } from "../../hooks/useAuthContext";
import { filesCloud, database, timestamp } from "../../firebase/config";
import { PDFDocument } from "pdf-lib";
import * as pdfjsLib from "pdfjs-dist";

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

const GEMINI_VERTEX_FN_URL =
  "https://us-central1-giuviprojects.cloudfunctions.net/geminiGenerateContentVertex";

const UNIVERSAL_PROMPT = `Analizza questo documento (immagine o PDF) e restituisci SOLO JSON puro (senza markdown).

Obiettivo:
1) Riconosci il tipo documento e compilalo nel campo "tipo_documento" con uno di questi valori ESATTI:
   - "bolla_vendita" (rapporto di intervento tecnico / bolla di lavoro)
   - "bolla_acquisto" (DDT / bolla di acquisto)
   - "generico" (qualsiasi altro documento)
2) Estrai i dati pertinenti in base al tipo riconosciuto.

Regole comuni:
- Le date nel documento sono in formato italiano (DD/MM/YYYY): convertile SEMPRE in YYYY-MM-DD.
- Le ore sono nel formato HH:MM (24h).
- Dove possibile usa numeri (non stringhe) per importi/quantità/ore.
- Se un campo non è presente nel documento, omettilo (non inventare e non stimare).

Se "tipo_documento" = "bolla_acquisto", estrai:
- numero_documento (stringa)
- data_documento (YYYY-MM-DD)
- fornitore (stringa)
- importo_totale (numero, se presente)
- elenco_articoli (array di oggetti con: descrizione (stringa), quantita (numero), unita (stringa, se presente), prezzo_unitario (numero, se presente), codice (stringa, se presente))

Se "tipo_documento" = "bolla_vendita", estrai:
- data_documento (YYYY-MM-DD)
- numero_documento (stringa)
- cliente (stringa)
- luogo_intervento (stringa)
- oggetto_intervento (stringa)
- descrizione_lavori (array di stringhe, se presente)
- materiale_utilizzato (array di oggetti) rispettando queste regole:
  • Considera SOLO materiali/ricambi; ignora manodopera/lavorazioni.
  • Riconosci righe con: descrizione, quantità, unità, prezzo unitario, totale (se presenti).
  • Quantità: normalizza in numero decimale (accetta virgola e punto).
  • Unità riconosciute: pz, kg, g, m, mt, l, ml, m², m3, cm, mm. Non inventare unità se assenti.
  • Codice articolo: se presente (es. "cod.", "codice", "art.", "SKU", "P/N") come "codice_articolo".
  • Escludi voci con parole chiave: manodopera, ore, diagnosi, collaudo, trasferta.
  • Output materiali: { "descrizione": stringa, "quantita": numero, "unita"?: stringa, "prezzo_unitario"?: numero, "codice_articolo"?: stringa }
- manodopera (array di oggetti), se presente:
  {
    "nome_tecnico": stringa,
    "giorno_intervento": YYYY-MM-DD,
    "ora_inizio": HH:MM,
    "ora_fine": HH:MM,
    "ore_viaggio": numero in ore decimali,
    "totale_ore": numero in ore decimali
  }
  Se la colonna "totale" non è valorizzata nel documento, calcola "totale_ore" per singolo tecnico come:
  (durata tra ora_fine e ora_inizio in ore decimali) + ore_viaggio.
  Usa ore decimali con 2 decimali. Interpreta "ore di viaggio" con virgola o punto (es. "0,5" = 0.5).
- totale_manodopera_ore (somma dei "totale_ore" di tutti i tecnici, ore decimali con 2 decimali, se calcolabile)

Se "tipo_documento" = "generico", estrai tutte le informazioni rilevanti in JSON, cercando di includere (se presenti):
- data_documento (YYYY-MM-DD)
- mittente (stringa)
- destinatario (stringa)
- oggetto (stringa)
- importi (oggetto o array, a tua scelta in base a come sono presentati nel documento)`;

const DOC_TYPES = {
  bolla_acquisto: {
    label: "Bolla di Acquisto",
    collection: "bolle_acquisto",
    storagePath: "bolle_acquisto",
  },
  bolla_vendita: {
    label: "Rapporto Intervento / Bolla",
    collection: "bolle_vendita",
    storagePath: "bolle_vendita",
  },
  generico: {
    label: "Documento Generico",
    collection: "documenti_generici",
    storagePath: "documenti_generici",
  },
};

const dataUrlToBlob = async (dataUrl) => {
  const res = await fetch(dataUrl);
  return await res.blob();
};

const isPlainObject = (value) =>
  !!value && typeof value === "object" && !Array.isArray(value);

const getAtPath = (root, path) =>
  path.reduce((acc, key) => (acc == null ? acc : acc[key]), root);

const setAtPath = (root, path, nextValue) => {
  if (!path.length) return nextValue;
  const [head, ...rest] = path;
  const base =
    root == null
      ? typeof head === "number"
        ? []
        : {}
      : Array.isArray(root)
        ? root.slice()
        : { ...root };

  const currentChild = root == null ? undefined : root[head];
  base[head] = setAtPath(currentChild, rest, nextValue);
  return base;
};

const removeAtPath = (root, path) => {
  if (!path.length) return root;
  const parentPath = path.slice(0, -1);
  const lastKey = path[path.length - 1];
  const parent = getAtPath(root, parentPath);

  if (Array.isArray(parent) && typeof lastKey === "number") {
    const nextArr = parent.slice();
    nextArr.splice(lastKey, 1);
    return setAtPath(root, parentPath, nextArr);
  }

  if (isPlainObject(parent) && typeof lastKey === "string") {
    const nextObj = { ...parent };
    delete nextObj[lastKey];
    return setAtPath(root, parentPath, nextObj);
  }

  return root;
};

const stripUndefinedDeep = (value) => {
  if (Array.isArray(value)) {
    return value.map(stripUndefinedDeep);
  }
  if (isPlainObject(value)) {
    const out = {};
    Object.entries(value).forEach(([k, v]) => {
      if (typeof v === "undefined") return;
      out[k] = stripUndefinedDeep(v);
    });
    return out;
  }
  return value;
};

const parseTimeToMinutes = (value) => {
  if (!value) return null;
  const str = String(value).trim();
  const match = str.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  if (h < 0 || h > 23) return null;
  if (m < 0 || m > 59) return null;
  return h * 60 + m;
};

const parseNumberLoose = (value) => {
  if (value == null) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const str = String(value).trim();
  if (!str) return null;
  const normalized = str.replace(/\s/g, "").replace(",", ".");
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
};

const parseBooleanLoose = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") {
    if (value === 1) return true;
    if (value === 0) return false;
  }
  if (typeof value === "string") {
    const s = value.trim().toLowerCase();
    if (s === "true" || s === "si" || s === "sì" || s === "1") return true;
    if (s === "false" || s === "no" || s === "0") return false;
  }
  return false;
};

const roundTo2 = (n) => Math.round(n * 100) / 100;

const normalizeDocType = (value) => {
  if (!value) return null;
  const raw = String(value).trim().toLowerCase();
  const compact = raw.replace(/\s+/g, "_");

  if (compact === "bolla_acquisto" || compact === "ddt")
    return "bolla_acquisto";
  if (
    compact === "bolla_vendita" ||
    compact === "rapporto_intervento" ||
    compact === "rapporto_di_intervento" ||
    compact === "rapporto_intervento_tecnico" ||
    compact === "bolla"
  ) {
    return "bolla_vendita";
  }
  if (compact === "generico" || compact === "documento_generico")
    return "generico";

  return null;
};

const computeBollaVenditaDerived = (data) => {
  const docType = normalizeDocType(data?.tipo_documento);
  if (docType !== "bolla_vendita") return data;
  if (!Array.isArray(data?.manodopera)) return data;

  let changed = false;
  let next = data;

  if (
    Object.prototype.hasOwnProperty.call(next, "includi_pausa") ||
    Object.prototype.hasOwnProperty.call(next, "pausa_ore")
  ) {
    if (!changed) {
      next = { ...next };
      changed = true;
    }
    delete next.includi_pausa;
    delete next.pausa_ore;
  }

  let manodoperaChanged = false;
  const nextManodopera = next.manodopera.map((row) => {
    if (!isPlainObject(row)) return row;

    let rowChanged = false;
    let nextRow = row;

    const includeBreak = parseBooleanLoose(row.includi_pausa);

    const travelEmpty =
      typeof row.ore_viaggio === "string" && row.ore_viaggio.trim() === "";
    const travelHoursParsed = parseNumberLoose(row.ore_viaggio);
    const travelHours =
      Number.isFinite(travelHoursParsed) && travelHoursParsed >= 0
        ? travelHoursParsed
        : travelEmpty
          ? 0
          : 0;

    if (
      typeof row.includi_pausa === "undefined" ||
      (typeof row.includi_pausa !== "undefined" &&
        typeof row.includi_pausa !== "boolean") ||
      typeof row.ore_viaggio === "undefined" ||
      (typeof row.ore_viaggio !== "undefined" &&
        typeof row.ore_viaggio !== "number" &&
        Number.isFinite(travelHoursParsed))
    ) {
      nextRow = { ...nextRow };
      rowChanged = true;
      if (typeof row.includi_pausa === "undefined")
        nextRow.includi_pausa = false;
      else if (typeof row.includi_pausa !== "boolean")
        nextRow.includi_pausa = includeBreak;
      if (typeof row.ore_viaggio === "undefined") nextRow.ore_viaggio = 0;
      else if (
        typeof row.ore_viaggio !== "number" &&
        Number.isFinite(travelHoursParsed)
      ) {
        nextRow.ore_viaggio = travelHours;
      }
    }

    if (Object.prototype.hasOwnProperty.call(nextRow, "pausa_ore")) {
      if (!rowChanged) {
        nextRow = { ...nextRow };
        rowChanged = true;
      }
      delete nextRow.pausa_ore;
    }

    const startMin = parseTimeToMinutes(row.ora_inizio);
    const endMin = parseTimeToMinutes(row.ora_fine);
    if (startMin == null || endMin == null) return rowChanged ? nextRow : row;

    let durationMin = endMin - startMin;
    if (durationMin < 0) durationMin += 24 * 60;

    const breakToSubtract = includeBreak ? 1 : 0;
    const durationHours = Math.max(0, durationMin / 60 - breakToSubtract);
    const total = roundTo2(durationHours + travelHours);

    if (row.totale_ore === total) return rowChanged ? nextRow : row;
    manodoperaChanged = true;
    if (!rowChanged) nextRow = { ...nextRow };
    nextRow.totale_ore = total;
    return nextRow;
  });

  if (manodoperaChanged) {
    if (!changed) {
      next = { ...next };
      changed = true;
    }
    next.manodopera = nextManodopera;
  }

  const totals = (next.manodopera || [])
    .map((r) => parseNumberLoose(r?.totale_ore))
    .filter((n) => Number.isFinite(n));

  if (totals.length) {
    const sum = roundTo2(totals.reduce((acc, n) => acc + n, 0));
    if (next.totale_manodopera_ore !== sum) {
      if (!changed) {
        next = { ...next };
        changed = true;
      }
      next.totale_manodopera_ore = sum;
    }
  }

  return changed ? next : data;
};

const bytesToMb = (bytes) => {
  if (!bytes) return 0;
  return Math.round((bytes / (1024 * 1024)) * 100) / 100;
};

const canvasToJpegBytes = (canvas, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      async (blob) => {
        try {
          if (!blob) {
            reject(new Error("JPEG_BLOB_EMPTY"));
            return;
          }
          const buf = await blob.arrayBuffer();
          resolve(new Uint8Array(buf));
        } catch (e) {
          reject(e);
        }
      },
      "image/jpeg",
      quality,
    );
  });

const compressPdfDataUrl = async (
  pdfDataUrl,
  {
    targetDpi = 170,
    jpegQuality = 0.72,
    maxPages = 25,
    pagePaddingMm = 0,
  } = {},
) => {
  const originalBlob = await dataUrlToBlob(pdfDataUrl);
  const originalBytes = originalBlob.size;

  const pdfArrayBuffer = await originalBlob.arrayBuffer();
  const loadingTask = pdfjsLib.getDocument({ data: pdfArrayBuffer });
  const pdf = await loadingTask.promise;

  const pageCount = pdf.numPages;
  if (pageCount > maxPages) {
    throw new Error(`PDF_TOO_LONG:${pageCount}:${maxPages}`);
  }
  const pagesToProcess = pageCount;

  const outPdf = await PDFDocument.create();

  const a4Portrait = { w: 595.28, h: 841.89 };
  const a4Landscape = { w: 841.89, h: 595.28 };

  const scale = targetDpi / 96;

  for (let i = 1; i <= pagesToProcess; i++) {
    const page = await pdf.getPage(i);
    const viewport = page.getViewport({ scale });

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("CANVAS_CONTEXT_NULL");
    canvas.width = Math.max(1, Math.floor(viewport.width));
    canvas.height = Math.max(1, Math.floor(viewport.height));

    await page.render({
      canvasContext: ctx,
      viewport,
      background: "rgb(255,255,255)",
    }).promise;

    const jpegBytes = await canvasToJpegBytes(canvas, jpegQuality);
    const embedded = await outPdf.embedJpg(jpegBytes);

    const landscape = canvas.width > canvas.height;
    const size = landscape ? a4Landscape : a4Portrait;

    const outPage = outPdf.addPage([size.w, size.h]);
    const padPt = (pagePaddingMm || 0) * 2.834645669;
    outPage.drawImage(embedded, {
      x: padPt,
      y: padPt,
      width: size.w - padPt * 2,
      height: size.h - padPt * 2,
    });

    canvas.width = 0;
    canvas.height = 0;
  }

  const outBytes = await outPdf.save({ useObjectStreams: true });
  const outBlob = new Blob([outBytes], { type: "application/pdf" });
  const compressedBytes = outBytes.byteLength;

  const outDataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(outBlob);
  });

  return {
    dataUrl: outDataUrl,
    blob: outBlob,
    stats: {
      originalBytes,
      compressedBytes,
      ratio: originalBytes ? compressedBytes / originalBytes : null,
      pageCount,
      pagesProcessed: pagesToProcess,
      targetDpi,
      jpegQuality,
    },
  };
};

export default function DocumentScanner() {
  const { user } = useAuthContext();
  const [image, setImage] = useState(null);
  const [mimeType, setMimeType] = useState(null); // 'image/jpeg', 'image/png', 'application/pdf'
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null); // Stringa JSON grezza o testo
  const [parsedData, setParsedData] = useState(null); // Oggetto JSON parsato
  const [formData, setFormData] = useState(null); // Dati editabili (form)
  const [uploadError, setUploadError] = useState(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [compressingPdf, setCompressingPdf] = useState(false);
  const [compressInfo, setCompressInfo] = useState(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!formData) return;
    const next = computeBollaVenditaDerived(formData);
    if (next !== formData) setFormData(next);
  }, [formData]);

  const processUploadedFile = async (file) => {
    if (!file) return;

    try {
      if (file.type !== "application/pdf") {
        setUploadError("Formato non supportato. Seleziona un file PDF.");
        return;
      }

      const fileData = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      setUploadError(null);
      setImage(fileData);
      setMimeType(file.type);
      setResult(null);
      setParsedData(null);
      setFormData(null);
      setSaveSuccess(false);
      setSaveError(null);
      setCompressingPdf(false);
      setCompressInfo(null);
    } catch (err) {
      console.error("Errore lettura file:", err);
      setUploadError("Errore durante il caricamento del file.");
    }
  };

  // Gestione upload file
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    await processUploadedFile(file);
    e.target.value = "";
  };

  const handleDropzoneClick = () => {
    if (analyzing || saving) return;
    fileInputRef.current?.click();
  };

  const handleDropzoneDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (analyzing || saving) return;
    setIsDragActive(true);
  };

  const handleDropzoneDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (analyzing || saving) return;
    if (!isDragActive) setIsDragActive(true);
  };

  const handleDropzoneDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragActive(false);
  };

  const handleDropzoneDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (analyzing || saving) return;

    setIsDragActive(false);
    const file = e.dataTransfer?.files?.[0];
    await processUploadedFile(file);
  };

  // Analisi con Gemini
  const analyzeWithGemini = async () => {
    if (!image) return;
    if (!user) {
      alert("Autenticazione richiesta per analizzare il documento.");
      return;
    }

    setAnalyzing(true);
    setResult(null);
    setParsedData(null);
    setFormData(null);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      // Rimuovi il prefisso data:image/jpeg;base64, (o application/pdf)
      const base64Data = image.split(",")[1];

      const prompt = UNIVERSAL_PROMPT;

      const idToken = await user.getIdToken();
      const response = await fetch(GEMINI_VERTEX_FN_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          prompt,
          mimeType: mimeType || "application/pdf",
          base64Data,
          model: "gemini-2.0-flash-001",
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || "Errore nella chiamata a Vertex AI");
      }

      const data = await response.json();
      const textResult = data.text || "";

      // Prova a parsare il JSON se Gemini lo ha restituito come stringa markdown
      let cleanResult = textResult;
      try {
        const jsonMatch = textResult.match(/```json\n([\s\S]*?)\n```/);
        if (jsonMatch && jsonMatch[1]) {
          cleanResult = jsonMatch[1];
        } else {
          // Tentativo fallback per json senza markdown o con formato diverso
          const firstBrace = textResult.indexOf("{");
          const lastBrace = textResult.lastIndexOf("}");
          if (firstBrace !== -1 && lastBrace !== -1) {
            cleanResult = textResult.substring(firstBrace, lastBrace + 1);
          }
        }

        // Parsa il JSON per assicurarsi che sia valido e per visualizzarlo meglio
        const parsed = JSON.parse(cleanResult);
        const normalizedType =
          normalizeDocType(parsed?.tipo_documento) ||
          normalizeDocType(parsed?.tipo) ||
          normalizeDocType(parsed?.document_type) ||
          null;
        const nextParsed =
          normalizedType && parsed?.tipo_documento !== normalizedType
            ? { ...parsed, tipo_documento: normalizedType }
            : parsed;

        setParsedData(nextParsed);
        setFormData(nextParsed);
        setResult(cleanResult); // Mantiene la stringa pulita per debug/copia
      } catch (e) {
        console.log("Errore parsing markdown json", e);
        setResult(textResult); // Mostra testo grezzo se non è JSON valido
        setParsedData(null);
        setFormData(null);
      }
    } catch (error) {
      console.error("Errore analisi Gemini:", error);
      alert("Errore durante l'analisi del documento: " + error.message);
    } finally {
      setAnalyzing(false);
    }
  };

  const saveDocument = async () => {
    if (!formData || !image) return;

    setSaving(true);
    setSaveError(null);
    setCompressingPdf(false);
    setCompressInfo(null);

    try {
      const detectedType =
        normalizeDocType(formData?.tipo_documento) &&
        DOC_TYPES[normalizeDocType(formData?.tipo_documento)]
          ? normalizeDocType(formData?.tipo_documento)
          : "generico";
      const config = DOC_TYPES[detectedType];

      // 1. Upload immagine/file
      // Converti base64 in blob
      const res = await fetch(image);
      const blobOriginal = await res.blob();

      let blobToUpload = blobOriginal;
      const finalMimeType = mimeType || blobOriginal.type || "image/jpeg";

      const MIN_SIZE_BYTES = 0;

      if (
        finalMimeType === "application/pdf" &&
        blobOriginal.size >= MIN_SIZE_BYTES
      ) {
        setCompressingPdf(true);
        try {
          const { blob: compressedBlob, stats } = await compressPdfDataUrl(
            image,
            {
              targetDpi: 170,
              jpegQuality: 0.72,
              maxPages: 25,
              pagePaddingMm: 0,
            },
          );

          if (
            compressedBlob &&
            compressedBlob.size > 0 &&
            compressedBlob.size < blobOriginal.size
          ) {
            blobToUpload = compressedBlob;
            setCompressInfo({
              ...stats,
              originalMb: bytesToMb(stats.originalBytes),
              compressedMb: bytesToMb(stats.compressedBytes),
              savedPct: stats.originalBytes
                ? Math.round(
                    (1 - stats.compressedBytes / stats.originalBytes) * 100,
                  )
                : null,
            });
          } else {
            setCompressInfo({
              ...stats,
              originalMb: bytesToMb(stats.originalBytes),
              compressedMb: bytesToMb(stats.compressedBytes),
              savedPct: 0,
              note: "Compressione non conveniente: caricato originale.",
            });
          }
        } catch (e) {
          const msg = e?.message || "";
          if (msg.startsWith("PDF_TOO_LONG:")) {
            const parts = msg.split(":");
            const pc = parts[1] ? Number(parts[1]) : null;
            const mp = parts[2] ? Number(parts[2]) : null;
            setCompressInfo({
              originalBytes: blobOriginal.size,
              compressedBytes: blobOriginal.size,
              originalMb: bytesToMb(blobOriginal.size),
              compressedMb: bytesToMb(blobOriginal.size),
              savedPct: 0,
              note: `PDF lungo (${pc || "?"} pagine): compressione non eseguita (limite ${mp || "?"}).`,
            });
            blobToUpload = blobOriginal;
          } else {
            setCompressInfo({
              originalBytes: blobOriginal.size,
              compressedBytes: blobOriginal.size,
              originalMb: bytesToMb(blobOriginal.size),
              compressedMb: bytesToMb(blobOriginal.size),
              savedPct: 0,
              note: "Compressione fallita: caricato originale.",
            });
            blobToUpload = blobOriginal;
          }
        } finally {
          setCompressingPdf(false);
        }
      }

      const extension = finalMimeType === "application/pdf" ? "pdf" : "jpg";
      const filename = `${Date.now()}_${detectedType}.${extension}`;
      const uploadPath = `${config.storagePath}/${user.uid}/${filename}`;

      const uploadRef = filesCloud.ref(uploadPath);
      await uploadRef.put(blobToUpload);
      const downloadUrl = await uploadRef.getDownloadURL();

      // 2. Salva dati su Firestore
      const sanitizedFormData = stripUndefinedDeep(formData);
      const docData = {
        ...sanitizedFormData,
        tipo_documento: detectedType,
        tipo_label: config.label,
        mimeType: finalMimeType,
        fotoUrl: downloadUrl,
        settore: "", // Sarà assegnato in seguito
        createdBy: user.uid,
        createdAt: timestamp.now(), // Usa il timestamp del server
        // Campi per ricerca/ordinamento
        data_inserimento: new Date().toISOString(),
        anno: sanitizedFormData.data_documento
          ? String(sanitizedFormData.data_documento).split("-")[0]
          : new Date().getFullYear().toString(),
      };

      await database.collection(config.collection).add(docData);

      setSaveSuccess(true);
      setSaving(false);

      // Reset opzionale dopo salvataggio
      // setImage(null);
      // setParsedData(null);
      // setResult(null);
    } catch (err) {
      console.error("Errore salvataggio:", err);
      setSaveError("Errore durante il salvataggio: " + err.message);
      setSaving(false);
    }
  };

  const renderInputForValue = (key, value, path) => {
    const label = key ? key.replace(/_/g, " ") : "";
    const valueType = typeof value;
    const readOnly =
      key === "totale_ore" ||
      key === "totale_manodopera_ore" ||
      key === "tipo_label";
    const fullWidth = key === "oggetto_intervento";
    const isDateString =
      valueType === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
    const isTimeString = valueType === "string" && /^\d{2}:\d{2}$/.test(value);
    const useTextarea =
      valueType === "string" &&
      (value.length > 80 ||
        /descrizione|oggetto|note|annotazioni|indirizzo/i.test(key || ""));

    if (valueType === "boolean" || key === "includi_pausa") {
      const checked =
        key === "includi_pausa" ? parseBooleanLoose(value) : !!value;
      return (
        <div className="form-field" key={path.join(".")}>
          <label className="form-label">
            <span className="form-label-text">{label}</span>
            <div className="form-checkbox">
              <input
                className="form-checkbox-input"
                type="checkbox"
                checked={checked}
                disabled={readOnly}
                onChange={(e) =>
                  setFormData((prev) => setAtPath(prev, path, e.target.checked))
                }
              />
            </div>
          </label>
        </div>
      );
    }

    const inputType =
      valueType === "number"
        ? "number"
        : isDateString
          ? "date"
          : isTimeString
            ? "time"
            : "text";

    const onChange = (e) => {
      const raw = e.target.value;
      let nextValue = raw;
      if (valueType === "number") {
        if (raw === "") nextValue = "";
        else {
          const asNumber = Number(raw);
          nextValue = Number.isFinite(asNumber) ? asNumber : raw;
        }
      }
      setFormData((prev) => setAtPath(prev, path, nextValue));
    };

    return (
      <div
        className={`form-field${fullWidth ? " full-width" : ""}`}
        key={path.join(".")}
      >
        <label className="form-label">
          <span className="form-label-text">{label}</span>
          {useTextarea ? (
            <textarea
              className="form-textarea"
              value={value ?? ""}
              onChange={onChange}
              readOnly={readOnly}
            />
          ) : (
            <input
              className="form-input"
              type={inputType}
              value={value ?? ""}
              onChange={onChange}
              step={valueType === "number" ? "any" : undefined}
              readOnly={readOnly}
            />
          )}
        </label>
      </div>
    );
  };

  const renderNode = (node, path, keyName) => {
    if (Array.isArray(node)) {
      const addItem = () => {
        const current = getAtPath(formData, path) || [];
        let nextItem = "";
        if (current.length > 0) {
          const sample = current[0];
          if (isPlainObject(sample)) {
            nextItem = Object.keys(sample).reduce((acc, k) => {
              acc[k] = "";
              return acc;
            }, {});
          } else if (typeof sample === "number") {
            nextItem = 0;
          } else if (typeof sample === "boolean") {
            nextItem = false;
          }
        }
        setFormData((prev) => setAtPath(prev, path, [...current, nextItem]));
      };

      return (
        <div className="form-array" key={path.join(".")}>
          <div className="form-array-header">
            <div className="form-section-title">
              {(keyName || "lista").replace(/_/g, " ")}
            </div>
            <button
              type="button"
              className="btn-secondary btn-small"
              onClick={addItem}
            >
              Aggiungi
            </button>
          </div>

          <div className="form-array-items">
            {node.map((item, index) => {
              const itemPath = [...path, index];
              const removeItem = () => {
                setFormData((prev) => removeAtPath(prev, itemPath));
              };

              const isManodoperaRow =
                keyName === "manodopera" && isPlainObject(item);
              const nomeTecnico = isManodoperaRow ? item.nome_tecnico : null;
              const title =
                isManodoperaRow && nomeTecnico
                  ? `${nomeTecnico}`
                  : keyName
                    ? `${keyName.replace(/_/g, " ")} #${index + 1}`
                    : `Elemento #${index + 1}`;

              return (
                <div className="form-array-item" key={itemPath.join(".")}>
                  <div className="form-array-item-actions">
                    <div className="form-array-item-title">{title}</div>
                    <button
                      type="button"
                      className="btn-secondary btn-small"
                      onClick={removeItem}
                    >
                      Rimuovi
                    </button>
                  </div>
                  <div className="form-array-item-body">
                    {isManodoperaRow ? (
                      <div className="manodopera-item">
                        <div className="manodopera-item-top">
                          {renderInputForValue(
                            "nome_tecnico",
                            item.nome_tecnico,
                            [...itemPath, "nome_tecnico"],
                          )}
                          {renderInputForValue(
                            "giorno_intervento",
                            item.giorno_intervento,
                            [...itemPath, "giorno_intervento"],
                          )}
                          {renderInputForValue("ora_inizio", item.ora_inizio, [
                            ...itemPath,
                            "ora_inizio",
                          ])}
                          {renderInputForValue("ora_fine", item.ora_fine, [
                            ...itemPath,
                            "ora_fine",
                          ])}
                          {renderInputForValue(
                            "ore_viaggio",
                            item.ore_viaggio,
                            [...itemPath, "ore_viaggio"],
                          )}
                          {renderInputForValue(
                            "includi_pausa",
                            item.includi_pausa,
                            [...itemPath, "includi_pausa"],
                          )}
                          {renderInputForValue("totale_ore", item.totale_ore, [
                            ...itemPath,
                            "totale_ore",
                          ])}
                        </div>
                        <div className="manodopera-item-extra">
                          {Object.entries(item)
                            .filter(
                              ([k]) =>
                                ![
                                  "nome_tecnico",
                                  "giorno_intervento",
                                  "ora_inizio",
                                  "ora_fine",
                                  "ore_viaggio",
                                  "includi_pausa",
                                  "totale_ore",
                                ].includes(k),
                            )
                            .map(([k, v]) =>
                              Array.isArray(v) || isPlainObject(v) ? (
                                <div
                                  className="form-section"
                                  key={[...itemPath, k].join(".")}
                                >
                                  <div className="form-section-title">
                                    {k.replace(/_/g, " ")}
                                  </div>
                                  {renderNode(v, [...itemPath, k], k)}
                                </div>
                              ) : (
                                renderInputForValue(k, v, [...itemPath, k])
                              ),
                            )}
                        </div>
                      </div>
                    ) : isPlainObject(item) || Array.isArray(item) ? (
                      renderNode(item, itemPath)
                    ) : (
                      renderInputForValue(
                        keyName
                          ? `${keyName}_${index + 1}`
                          : `elemento_${index + 1}`,
                        item,
                        itemPath,
                      )
                    )}
                  </div>
                </div>
              );
            })}
            {node.length === 0 ? (
              <div className="form-empty">Nessun elemento presente.</div>
            ) : null}
          </div>
        </div>
      );
    }

    if (isPlainObject(node)) {
      return (
        <div className="form-object" key={path.join(".")}>
          {Object.entries(node)
            .filter(([k]) => k !== "tipo_documento")
            .map(([k, v]) => {
              const nextPath = [...path, k];
              if (Array.isArray(v)) {
                return (
                  <div className="form-section" key={nextPath.join(".")}>
                    {renderNode(v, nextPath, k)}
                  </div>
                );
              }
              if (isPlainObject(v)) {
                return (
                  <div className="form-section" key={nextPath.join(".")}>
                    <div className="form-section-title">
                      {k.replace(/_/g, " ")}
                    </div>
                    {renderNode(v, nextPath)}
                  </div>
                );
              }
              return renderInputForValue(k, v, nextPath);
            })}
        </div>
      );
    }

    return renderInputForValue(keyName || "valore", node, path);
  };

  const detectedDocType =
    normalizeDocType(formData?.tipo_documento) &&
    DOC_TYPES[normalizeDocType(formData?.tipo_documento)]
      ? normalizeDocType(formData?.tipo_documento)
      : parsedData?.tipo_documento &&
          DOC_TYPES[normalizeDocType(parsedData?.tipo_documento)]
        ? normalizeDocType(parsedData?.tipo_documento)
        : null;

  return (
    <div className="scanner-container">
      <div className="scanner-header">
        <h1>Scanner Documenti AI</h1>
        <p>Carica un PDF per estrarre i dati automaticamente.</p>
      </div>

      <div className="scanner-content">
        <div className="scanner-layout">
          <div className="scanner-left">
            <div className="preview-container">
              {!image && (
                <div className="placeholder-preview">
                  <p>Seleziona un PDF per vedere l’anteprima</p>
                </div>
              )}
              {image && (
                <div className="pdf-preview">
                  <p>Documento PDF caricato</p>
                  <embed src={image} type="application/pdf" />
                </div>
              )}
              {analyzing && (
                <div className="loading-overlay">
                  <div className="spinner"></div>
                  <p>Analisi in corso con Gemini AI...</p>
                </div>
              )}
              {compressingPdf && (
                <div className="loading-overlay">
                  <div className="spinner"></div>
                  <p>Compressione PDF in corso...</p>
                </div>
              )}
            </div>
          </div>
          <div className="scanner-right">
            {uploadError && <p className="error-message">{uploadError}</p>}
            <div className="controls">
              <div
                className={`file-dropzone ${isDragActive ? "drag-active" : ""} ${
                  analyzing || saving ? "disabled" : ""
                }`}
                role="button"
                tabIndex={analyzing || saving ? -1 : 0}
                onClick={handleDropzoneClick}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleDropzoneClick();
                  }
                }}
                onDragEnter={handleDropzoneDragEnter}
                onDragOver={handleDropzoneDragOver}
                onDragLeave={handleDropzoneDragLeave}
                onDrop={handleDropzoneDrop}
                aria-disabled={analyzing || saving}
              >
                <div className="file-dropzone-content">
                  <strong>Trascina qui il PDF da analizzare</strong>
                  <span>
                    oppure clicca per aprire il selettore del documento
                  </span>
                  {image && (
                    <small className="file-dropzone-status">
                      Documento PDF selezionato
                    </small>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf"
                  onChange={handleFileUpload}
                  disabled={analyzing || saving}
                />
              </div>
              <button
                className="btn-primary"
                onClick={analyzeWithGemini}
                disabled={!image || analyzing || saving}
              >
                {analyzing ? "Analisi..." : "Analizza con AI"}
              </button>
            </div>
            {result && (
              <div className="result-container">
                <h3>Risultato Analisi:</h3>
                {parsedData ? (
                  <>
                    <div className="doc-detected">
                      <div className="doc-detected-row">
                        <span className="doc-detected-label">
                          Tipo documento riconosciuto:
                        </span>
                        <span className="doc-detected-value">
                          {detectedDocType
                            ? DOC_TYPES[detectedDocType]?.label ||
                              detectedDocType
                            : "Non disponibile"}
                        </span>
                      </div>
                    </div>
                    <div className="doc-type-selector">
                      <div className="doc-type-inner">
                        <label>Tipo documento (se vuoi correggerlo):</label>
                        <select
                          value={
                            normalizeDocType(formData?.tipo_documento) ||
                            detectedDocType ||
                            "generico"
                          }
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...(prev || {}),
                              tipo_documento: e.target.value,
                            }))
                          }
                          disabled={analyzing || saving}
                        >
                          {Object.entries(DOC_TYPES).map(([key, config]) => (
                            <option key={key} value={key}>
                              {config.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="dynamic-form">
                      {formData ? renderNode(formData, []) : null}
                    </div>
                  </>
                ) : (
                  <pre className="result-content">{result}</pre>
                )}
                {formData && !saveSuccess && (
                  <button
                    className="btn-save"
                    onClick={saveDocument}
                    disabled={saving}
                  >
                    {saving ? "Salvataggio..." : "Salva Documento"}
                  </button>
                )}
                {compressInfo && (
                  <p className="success-message">
                    PDF: {compressInfo.originalMb} MB →{" "}
                    {compressInfo.compressedMb} MB
                    {typeof compressInfo.savedPct === "number"
                      ? ` (-${compressInfo.savedPct}%)`
                      : ""}
                    {compressInfo.note ? ` • ${compressInfo.note}` : ""}
                  </p>
                )}
                {saveError && <p className="error-message">{saveError}</p>}
                {saveSuccess && (
                  <p className="success-message">
                    Documento salvato con successo!
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
